import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import * as s from "./Sidebar.styles";

import { connect, useDispatch } from "react-redux";
import { SET_THE_TOKEN } from "../../app/start/actions";
import UserService from "../../services/UserService";

import RenderOnRole from "../RenderOnRole";
import { buildMenuHandlers } from "./menuHandlers";
import { useMenuItemFromURL } from "../../hooks/useMenuItemFromURL";
import { useTreeState } from "../../hooks/useTreeState";
import { useProfileState } from "../../hooks/useProfileState";
import { useProjectState } from "../../hooks/useProjectState";
import { useDataState } from "../../hooks/useDataState";
import { buildRealmPath } from "../../shared/routing";

const Sidebar = ({ theRealm, keycloakToken, ...props }) => {
    const dispatch = useDispatch();
    const token = keycloakToken;
    const setToken = (value) => dispatch({ type: SET_THE_TOKEN, action: value });

    const tree = useTreeState();
    const profile = useProfileState();
    const project = useProjectState();
    const data = useDataState();

    const {
        sidebarHeader = { fullName: "", shortName: "" },
        menuItems = [],
        fonts = { header: "", menu: "" },
        colorPalette = {
            bgColor1: "rgba(11, 171, 100, 0.8)",
            bgColor2: "rgba(59, 183, 143, 0.8)",
            fontColor: "rgba(22, 46, 39)",
            fontColorSelected: "rgba(255, 255, 255)",
            dividerColor: "rgba(122, 204, 178)",
            selectedBackgroundCollapsedMode: "dark",
        },
    } = props;

    const [selected, setSelectedMenuItem] = useState(menuItems[0].name);
    const [isSidebarOpen, setSidebarState] = useState(true);
    const [header, setHeader] = useState(sidebarHeader.fullName);
    const [subMenusStates, setSubmenus] = useState({});

    // URL-basierte Initialisierung
    const { selectedItemName, selectedItemIndex, selectedSubItemIndex } =
        useMenuItemFromURL(menuItems);

    useEffect(() => {
        if (selectedItemName) setSelectedMenuItem(selectedItemName);
    }, [selectedItemName]);

    // Submenu-Initialisierung (einmalig beim Mounten)
    useEffect(() => {
        const newSubmenus = {};
        menuItems.forEach((item, index) => {
            if (item.subMenuItems.length > 0) {
                newSubmenus[index] = { isOpen: false, selected: null };
            }
        });

        if (selectedItemIndex !== -1 && newSubmenus[selectedItemIndex] !== undefined) {
            newSubmenus[selectedItemIndex].isOpen = true;
            if (selectedSubItemIndex !== -1) {
                newSubmenus[selectedItemIndex].selected = selectedSubItemIndex;
            }
        }

        if (Object.keys(subMenusStates).length === 0) setSubmenus(newSubmenus);
    }, [menuItems, subMenusStates, selectedItemIndex, selectedSubItemIndex]);

    // Header-Text
    useEffect(() => {
        if (isSidebarOpen) {
            setTimeout(() => setHeader(sidebarHeader.fullName), 200);
        } else {
            setHeader(sidebarHeader.shortName);
        }
    }, [isSidebarOpen, sidebarHeader]);

    // Sidebar bei kleinem Fenster kollabieren
    useEffect(() => {
        const updateWindowWidth = () => setSidebarState(window.innerWidth >= 1280);
        window.addEventListener("resize", updateWindowWidth);
        return () => window.removeEventListener("resize", updateWindowWidth);
    }, []);

    // Login-Check
    useEffect(() => {
        if (!UserService.isLoggedIn()) UserService.doLogout();
    }, []);

    // Menü-Handler (Strategy Pattern)
    const menuHandlers = buildMenuHandlers({
        token,
        theRealm,
        treeItemState: tree.items,
        profilIsLoaded: profile.isLoaded,
        setTreeItemsState: tree.setItems,
        setTreeClicked: tree.setClicked,
        setTreeIsLoaded: tree.setIsLoaded,
        setProfilClicked: profile.setClicked,
        setProfilIsLoaded: profile.setIsLoaded,
        setProjectClicked: project.setClicked,
        setProjectIsLoaded: project.setIsLoaded,
        setUserProjectIsLoaded: project.setUserProjectIsLoaded,
        setDataobjectsClicked: data.setObjectsClicked,
        setDataobjectsIsLoaded: data.setIsLoaded,
        setDataobjects: data.setObjects,
        setDatacomponentsClicked: data.setComponentsClicked,
        setBimProf: profile.setProfiles,
        setBimProjects: project.setProjects,
        setBimUserProjects: project.setUserProjects,
    });

    const toggleSubMenu = (index) => {
        const copy = JSON.parse(JSON.stringify(subMenusStates));
        if (Object.prototype.hasOwnProperty.call(subMenusStates, index)) {
            copy[index].isOpen = !subMenusStates[index].isOpen;
        } else {
            for (const key in copy) {
                copy[key].isOpen = false;
                copy[key].selected = null;
            }
        }
        setSubmenus(copy);
    };

    const handleMenuItemClick = async (name, index) => {
        setToken(UserService.getToken());
        setSelectedMenuItem(name);
        await menuHandlers[name]?.();
        toggleSubMenu(index);
    };

    const handleSubMenuItemClick = (menuItemIdx, subMenuItemIdx) => {
        const copy = JSON.parse(JSON.stringify(subMenusStates));
        copy[menuItemIdx].selected = subMenuItemIdx;
        setSubmenus(copy);
    };

    const menuItemsJSX = menuItems.map((item, index) => {
        const isItemSelected = selected === item.name;
        const hasSubmenus = item.subMenuItems.length > 0;
        const isOpen = subMenusStates[index]?.isOpen;

        const subMenusJSX = item.subMenuItems.map((subMenuItem, subMenuItemIndex) => {
            const isSubmenuItemSelected = subMenusStates[index]?.selected === subMenuItemIndex;
            return (
                <RenderOnRole
                    key={`blup_${index}_${subMenuItemIndex}`}
                    roles={subMenuItem.rollen}
                >
                    <Link to={buildRealmPath(theRealm, `${item.to}${subMenuItem.to}`)} style={{ textDecoration: "none" }}>
                        <s.SubMenuItem
                            onClick={() => handleSubMenuItemClick(index, subMenuItemIndex)}
                            selected={isSubmenuItemSelected}
                            colorPalette={colorPalette}
                        >
                            {subMenuItem.name}
                        </s.SubMenuItem>
                    </Link>
                </RenderOnRole>
            );
        });

        return (
            <RenderOnRole key={`bla_${index}`} roles={item.rollen}>
                <s.ItemContainer>
                    <Link to={buildRealmPath(theRealm, item.to)} style={{ textDecoration: "none" }}>
                        <s.MenuItem
                            font={fonts.menu}
                            selected={isItemSelected}
                            onClick={() => handleMenuItemClick(item.name, index)}
                            isSidebarOpen={isSidebarOpen}
                            isOpen={isOpen}
                            colorPalette={colorPalette}
                        >
                            <s.IconWrapper isSidebarOpen={isSidebarOpen} selected={isItemSelected}>
                                {item.icon}
                            </s.IconWrapper>
                            <s.Text isSidebarOpen={isSidebarOpen}>{item.name}</s.Text>
                            {hasSubmenus && isSidebarOpen && (
                                <s.DropdownIcon
                                    selected={isItemSelected}
                                    isOpen={isOpen}
                                    colorPalette={colorPalette}
                                />
                            )}
                        </s.MenuItem>
                    </Link>

                    <AnimatePresence>
                        {hasSubmenus && isOpen && (
                            <motion.nav
                                initial={{ opacity: 0, y: -15 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.35 }}
                                exit={{ opacity: 0, x: -30 }}
                            >
                                <s.SubMenuItemContainer
                                    isSidebarOpen={isSidebarOpen}
                                    colorPalette={colorPalette}
                                >
                                    {subMenusJSX}
                                </s.SubMenuItemContainer>
                            </motion.nav>
                        )}
                    </AnimatePresence>
                </s.ItemContainer>
            </RenderOnRole>
        );
    });

    return (
        <s.SidebarContainer
            isSidebarOpen={isSidebarOpen}
            colorPalette={colorPalette}
        >
            <s.SidebarHeader font={fonts.header}>
                {isSidebarOpen ? header : ''}
            </s.SidebarHeader>
            <s.MenuItemContainer>{menuItemsJSX}</s.MenuItemContainer>
            <s.UserFooter>
                {isSidebarOpen ? UserService.getUsername() : ''}
            </s.UserFooter>
            <s.TogglerContainer isSidebarOpen={isSidebarOpen}>
                <s.Toggler onClick={() => setSidebarState(!isSidebarOpen)}>
                    <span />
                </s.Toggler>
            </s.TogglerContainer>
        </s.SidebarContainer>
    );
};

function mapStateToProps({ theRealm, keycloakToken }) {
    return { theRealm, keycloakToken };
}

export default connect(mapStateToProps)(Sidebar);
